import React, { useState } from 'react';
import {
  Box, Button, ButtonGroup, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography,
} from '@mui/material';
import { usePreferences } from '../../contexts/PreferencesContext';
import { useWorkspaceShape } from '../../hooks/useWorkspaceShape';
import { track } from '../../utils/analytics';

/**
 * Two questions, once, before an account settles into a shape.
 *
 * The first one asks about logins rather than people. "Who uses this account?" reads as though
 * the answer might be "my colleagues, on my password", which is the opposite of what the answer
 * buys you: saying yes is what turns on inviting people to accounts of their own.
 *
 * Never shown to a demo session, which is always the simple shape and cannot change it.
 */
const QUESTIONS = [
  {
    key: 'worksWithATeam',
    label: 'Will anyone else need their own login?',
    hint: 'Colleagues get their own sign-in, and you choose what each of them can do.',
    no: 'Just me',
    yes: 'Yes, my team',
  },
  {
    key: 'usesSeveralNumbers',
    label: 'How many WhatsApp numbers do you run?',
    hint: 'Several numbers are kept apart by environments. One business with one number needs none.',
    no: 'One',
    yes: 'Several',
  },
];

const WorkspaceSetupDialog = () => {
  const { preferences, updatePreferences } = usePreferences();
  const { answered, canChange, loading } = useWorkspaceShape();
  const [draft, setDraft] = useState({
    worksWithATeam: Boolean(preferences.worksWithATeam),
    usesSeveralNumbers: Boolean(preferences.usesSeveralNumbers),
  });

  if (loading || answered || !canChange) return null;

  const finish = (values, how) => {
    updatePreferences({ ...values, workspaceSetupCompleted: true });
    track('workspace_shape_answered', { ...values, how });
  };

  return (
    <Dialog
      open
      maxWidth="sm"
      fullWidth
      // No close on backdrop click or Escape: it is two taps, and a half-dismissed dialog that
      // reappears on the next page load is worse than one that is answered and gone.
      disableEscapeKeyDown
      aria-labelledby="workspace-setup-title"
      data-testid="workspace-setup-dialog"
    >
      <DialogTitle id="workspace-setup-title">Set FlowGate up for how you work</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          So we only show you the settings you need. You can change either answer later, in
          Settings &rarr; Workspace.
        </Typography>

        <Stack spacing={3}>
          {QUESTIONS.map(({ key, label, hint, no, yes }) => (
            <Box key={key}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{label}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {hint}
              </Typography>
              <ButtonGroup size="small" aria-label={label}>
                <Button
                  variant={draft[key] ? 'outlined' : 'contained'}
                  onClick={() => setDraft((prev) => ({ ...prev, [key]: false }))}
                  data-testid={`setup-${key}-no`}
                >
                  {no}
                </Button>
                <Button
                  variant={draft[key] ? 'contained' : 'outlined'}
                  onClick={() => setDraft((prev) => ({ ...prev, [key]: true }))}
                  data-testid={`setup-${key}-yes`}
                >
                  {yes}
                </Button>
              </ButtonGroup>
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          color="inherit"
          sx={{ color: 'text.secondary' }}
          onClick={() => finish({ worksWithATeam: false, usesSeveralNumbers: false }, 'dismissed')}
          data-testid="setup-dismiss"
        >
          Just me, one number
        </Button>
        <Button variant="contained" onClick={() => finish(draft, 'saved')} data-testid="setup-save">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkspaceSetupDialog;
